import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
  useMarketplaceDefinitions,
  fetchResolvedDefinition,
} from '../hooks/useMarketplaceDefinitions';
import { useScrapingTasks, ScrapingTask, TaskSourceFilter } from '../hooks/useScrapingTasks';
import { useUserNames } from '../hooks/useUserNames';
import { getCurrentProviderId } from '../utils/currentUser';
import { UserProviderChip } from '../components/UserProviderChip';
import { apiClient, endpoints } from '../api/client';
import { generateSearchUrlsForMarketplace } from '../utils/urlTemplates';
import { useThemeContext } from '../contexts/ThemeContext';
import CreateTaskDialog from '../components/CreateTaskDialog';
import EditTaskDialog from '../components/EditTaskDialog';
import {
  Security,
  Visibility,
  Edit,
  Refresh,
  Delete,
  PlayArrow,
  Extension,
  Dns,
  Dashboard,
  Web,
  Campaign,
  CheckCircle,
  ErrorOutline,
  PauseCircle,
  Schedule,
  Autorenew,
  WarningAmber,
  AutoAwesome,
  Science,
} from '@mui/icons-material';
import TaskGroupDialog from '../components/TaskGroupDialog';
import AssembleGroupDialog, { AssembleSubmitPayload } from '../components/AssembleGroupDialog';
import VerifyAccessDialog from '../components/VerifyAccessDialog';
import { useTaskGroups, TaskGroup } from '../hooks/useTaskGroups';
import {
  CaptchaTask,
  enrichCaptchaTaskFromResults,
  taskNeedsCaptchaAction,
} from '../utils/captchaAccess';
import { isActiveGroupDuplicate, replaceGroupTaskId } from '../utils/taskGroupAssemble';

function IconCell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Tooltip title={title}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

function ColumnHeaderTooltip({ label, tip }: { label: string; tip: string }) {
  return (
    <Tooltip title={tip}>
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: '0.8125rem',
          cursor: 'default',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

function resolveTaskSourceLabel(taskSource?: string | null): string {
  const src = taskSource || 'scraper_ui';
  switch (src) {
    case 'importer_crawl_request':
    case 'dashboard_request':
    case 'investigation_request':
      return 'Dashboard';
    case 'speak_up_request':
      return 'Speak Up';
    case 'collector_extension':
      return 'Extension';
    case 'scraper_ui':
      return 'Collector';
    case 'definition_test_suite':
      return 'Test suite';
    default:
      return src;
  }
}

function TaskSourceIconCell({
  taskSource,
  crawlRequestId,
}: {
  taskSource?: string | null;
  crawlRequestId?: string;
}) {
  const src = taskSource || 'scraper_ui';
  const label = resolveTaskSourceLabel(src);
  const tooltip = crawlRequestId ? `${label} · crawl ${crawlRequestId}` : label;

  let icon: React.ReactNode;
  if (
    src === 'importer_crawl_request' ||
    src === 'dashboard_request' ||
    src === 'investigation_request'
  ) {
    icon = <Dashboard fontSize="small" color="secondary" />;
  } else if (src === 'speak_up_request') {
    icon = <Campaign fontSize="small" color="warning" />;
  } else if (src === 'collector_extension') {
    icon = <Extension fontSize="small" color="info" />;
  } else if (src === 'definition_test_suite') {
    icon = <Science fontSize="small" color="secondary" />;
  } else {
    icon = <Web fontSize="small" color="action" />;
  }

  return <IconCell title={tooltip}>{icon}</IconCell>;
}

function resolveTaskStatusDisplay(task: ScrapingTask): {
  label: string;
  color: 'primary' | 'success' | 'error' | 'warning' | 'action';
  Icon: typeof CheckCircle;
} {
  let label = task.status;
  let color: 'primary' | 'success' | 'error' | 'warning' | 'action' = 'action';

  switch (task.status) {
    case 'running':
      color = 'primary';
      break;
    case 'completed':
      color = 'success';
      break;
    case 'failed':
      color = 'error';
      break;
    case 'paused':
      color = 'warning';
      break;
    default:
      color = 'action';
  }

  if (task.status === 'completed' && task.parsing_success_rate !== undefined) {
    const numRate = Math.round(task.parsing_success_rate * 100);
    if (numRate === 0) {
      label = 'failed';
      color = 'error';
    } else if (numRate < 100) {
      label = 'partial';
      color = 'warning';
    }
  }

  if (label === 'failed' || task.status === 'failed') {
    return { label: 'Failed', color, Icon: ErrorOutline };
  }
  if (label === 'partial') {
    return { label: 'Partial', color, Icon: WarningAmber };
  }
  if (task.status === 'running') {
    return { label: 'Running', color, Icon: Autorenew };
  }
  if (task.status === 'paused') {
    return { label: 'Paused', color, Icon: PauseCircle };
  }
  if (task.status === 'queued') {
    return { label: 'Queued', color, Icon: Schedule };
  }
  if (task.status === 'completed') {
    return { label: 'Completed', color, Icon: CheckCircle };
  }

  const formatted = label.charAt(0).toUpperCase() + label.slice(1);
  return { label: formatted, color, Icon: Schedule };
}

const ScrapingTasks: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ScrapingTask | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [captchaDialogOpen, setCaptchaDialogOpen] = useState(false);
  const [captchaTask, setCaptchaTask] = useState<CaptchaTask | null>(null);
  const [cookieStatus, setCookieStatus] = useState<{ [key: string]: boolean }>({});
  // Track which tasks have completed auth export (cookies are available)
  const [authExportCompleted, setAuthExportCompleted] = useState<{ [key: string]: boolean }>({});
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TaskGroup | null>(null);
  const [assembleDialogOpen, setAssembleDialogOpen] = useState(false);
  const [assembleAppendGroup, setAssembleAppendGroup] = useState<TaskGroup | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [taskSourceFilter, setTaskSourceFilter] = useState<TaskSourceFilter>('all');
  const [mineOnly, setMineOnly] = useState(false);
  const [myProviderId, setMyProviderId] = useState<string | null>(null);
  const [deletePendingOpen, setDeletePendingOpen] = useState(false);
  const [deletingPending, setDeletingPending] = useState(false);
  const { groups, fetchGroups, deleteGroup, updateGroup, createGroup } = useTaskGroups();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  useEffect(() => {
    let cancelled = false;
    getCurrentProviderId().then(id => {
      if (!cancelled) setMyProviderId(id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Refs to avoid duplicate API calls when effects re-run (e.g. on task list refresh every 5s)
  const requestedCookieDomainsRef = useRef<Set<string>>(new Set());
  const cookieStatusCacheRef = useRef<Map<string, boolean>>(new Map());

  const { data: definitions } = useMarketplaceDefinitions();
  const { data: userNames = {}, isLoading: userNamesLoading } = useUserNames();

  // Non-template definitions can be selected; inactive / needs-fix are allowed as an override
  const scrapableDefinitions = React.useMemo(() => {
    return (definitions || [])
      .filter((d: any) => !d.is_template)
      .sort((a: any, b: any) => {
        const aActive = a.is_active !== false ? 0 : 1;
        const bActive = b.is_active !== false ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
          sensitivity: 'base',
        });
      });
  }, [definitions]);

  // Edit may reference a definition that was deactivated after the task was created
  const definitionsForTaskEdit = React.useMemo(() => {
    const taskDefId = editingTask?.marketplace_definition_id;
    if (!taskDefId || scrapableDefinitions.some((d: any) => d.id === taskDefId)) {
      return scrapableDefinitions;
    }
    const current = (definitions || []).find((d: any) => d.id === taskDefId);
    return current ? [...scrapableDefinitions, current] : scrapableDefinitions;
  }, [scrapableDefinitions, definitions, editingTask?.marketplace_definition_id]);

  const {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    fetchTasks,
    getTask,
    rerunTask,
    startTask,
    deleteTask,
    deletePendingTasks,
  } = useScrapingTasks({
    autoRefresh: true,
    taskSourceFilter,
    createdBy: mineOnly ? myProviderId : null,
  });
  const [runningGroupId, setRunningGroupId] = useState<string | null>(null);
  // Cookie status for tasks that already recorded a CAPTCHA (auth-export completion).
  // Use a ref to avoid duplicate requests when tasks refetch (e.g. every 5s) before state updates.
  React.useEffect(() => {
    const domains = Array.from(
      new Set(
        tasks
          .map(t =>
            t.urls && t.urls[0]
              ? (() => {
                  try {
                    return new URL(t.urls[0]).hostname;
                  } catch {
                    return '';
                  }
                })()
              : ''
          )
          .filter(Boolean)
      )
    );
    domains.forEach(d => {
      if (requestedCookieDomainsRef.current.has(d)) return;
      requestedCookieDomainsRef.current.add(d);
      checkCookieStatus(d);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  // Check auth export completion for tasks with CAPTCHA issues
  React.useEffect(() => {
    const checkAuthExportForCaptchaTasks = async () => {
      const captchaTasks = tasks.filter(
        task =>
          (task.status === 'completed' || task.status === 'failed') &&
          needsCaptchaSolving(task) &&
          authExportCompleted[task.id] === undefined
      );

      for (const task of captchaTasks) {
        const url = task.urls?.[0];
        if (url) {
          try {
            const domain = new URL(url).hostname;
            const hasCookies = await checkCookieStatus(domain, task.id);
            // If cookies are available, auth export is completed
            if (hasCookies) {
              setAuthExportCompleted(prev => ({
                ...prev,
                [task.id]: true,
              }));
            }
          } catch (error) {
            console.warn(`Failed to check auth export for task ${task.id}:`, error);
          }
        }
      }
    };

    checkAuthExportForCaptchaTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, cookieStatus]);

  // Update last refresh time when tasks are fetched
  const handleTasksRefresh = () => {
    setLastRefresh(new Date());
  };

  // Show verification action only when crawl results recorded a challenge.
  const needsCaptchaSolving = (task: ScrapingTask) => taskNeedsCaptchaAction(task);

  // Open CAPTCHA solving dialog
  const openCaptchaDialog = async (task: ScrapingTask) => {
    try {
      const response = await apiClient.get(endpoints.taskResults(task.id));
      const results = Array.isArray(response.data) ? response.data : response.data.results || [];
      setCaptchaTask(enrichCaptchaTaskFromResults(task, results));
    } catch (err) {
      console.warn('Could not fetch blocked URL from task results:', err);
      setCaptchaTask(task);
    }
    setCaptchaDialogOpen(true);
  };

  // Check cookie status for a domain. Uses refs to avoid duplicate API calls when called from
  // multiple effects (e.g. cookie-status effect and auth-export effect).
  const checkCookieStatus = async (domain: string, taskId?: string): Promise<boolean> => {
    if (requestedCookieDomainsRef.current.has(domain)) {
      const cached = cookieStatusCacheRef.current.get(domain);
      if (cached !== undefined) {
        if (cached && taskId) {
          setAuthExportCompleted(prev => ({ ...prev, [taskId]: true }));
        }
        return cached;
      }
    }
    requestedCookieDomainsRef.current.add(domain);
    try {
      const response = await apiClient.get(`/api/v1/scraper/cookies/status/${domain}`);
      const hasCookies = response.data.has_cookies;
      cookieStatusCacheRef.current.set(domain, hasCookies);
      setCookieStatus(prev => ({ ...prev, [domain]: hasCookies }));
      if (hasCookies && taskId) {
        setAuthExportCompleted(prev => ({ ...prev, [taskId]: true }));
      }
      return hasCookies;
    } catch (error) {
      console.error('Failed to check cookie status:', error);
      requestedCookieDomainsRef.current.delete(domain);
      return false;
    }
  };

  const filteredTasks = tasks.filter(task => {
    const searchBase = [task.brand_name, task.product_name, task.keywords_text]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const matchesSearch = searchBase.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesMarketplace =
      marketplaceFilter === 'all' || task.marketplace_definition_id === marketplaceFilter;
    const matchesMine = !mineOnly || !myProviderId || task.created_by === myProviderId;
    return matchesSearch && matchesStatus && matchesMarketplace && matchesMine;
  });

  const handleCreateTask = async (submitData: any) => {
    setIsCreatingTasks(true);
    try {
      const { taskData, selectedMarketplaces, groupInfo } = submitData;
      const createdBy = await getCurrentProviderId();

      const resolveMarketplaceForUrls = async (marketplace: any) => {
        const hasTemplate =
          typeof marketplace?.config?.search_url_template === 'string' &&
          marketplace.config.search_url_template.trim();
        if (hasTemplate || !marketplace?.id) return marketplace;
        try {
          const resolved = await fetchResolvedDefinition(marketplace.id);
          return { ...marketplace, ...resolved };
        } catch (error) {
          console.warn(
            '[Create Task] Failed to resolve marketplace definition',
            marketplace.id,
            error
          );
          return marketplace;
        }
      };

      // Helper to group URLs by marketplace domain for URL mode with multiple marketplaces
      const groupUrlsByMarketplace = (
        urls: string[],
        marketplaces: any[]
      ): Map<string, string[]> => {
        const urlsByMarketplace = new Map<string, string[]>();

        // Extract domain from URL
        const getDomainFromUrl = (url: string): string => {
          try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace(/^www\./, '');
          } catch {
            return '';
          }
        };

        // Group URLs by matching marketplace domain
        for (const url of urls) {
          const urlDomain = getDomainFromUrl(url);
          let matched = false;

          for (const marketplace of marketplaces) {
            const marketplaceDomain = (marketplace.domain || '').replace(/^www\./, '');

            if (urlDomain && marketplaceDomain && urlDomain.includes(marketplaceDomain)) {
              if (!urlsByMarketplace.has(marketplace.id)) {
                urlsByMarketplace.set(marketplace.id, []);
              }
              urlsByMarketplace.get(marketplace.id)!.push(url);
              matched = true;
              break;
            }
          }

          if (!matched && urlDomain) {
            console.warn('[Create Task] Could not match URL to any marketplace:', url);
          }
        }

        return urlsByMarketplace;
      };

      // If multiple marketplaces selected, create a task for each
      const marketplacesToProcess =
        selectedMarketplaces && selectedMarketplaces.length > 0
          ? await Promise.all(selectedMarketplaces.map(resolveMarketplaceForUrls))
          : [{ id: taskData.marketplace_definition_id }];

      const createdTasks: any[] = [];
      let allSuccess = true;

      // For URL mode with multiple marketplaces, group URLs by domain
      let urlsByMarketplaceId: Map<string, string[]> | null = null;
      if (
        marketplacesToProcess.length > 1 &&
        taskData.input_mode === 'urls' &&
        taskData.urls &&
        taskData.urls.length > 0
      ) {
        urlsByMarketplaceId = groupUrlsByMarketplace(taskData.urls, marketplacesToProcess);
        console.log(
          '[Create Task] Grouped URLs by marketplace:',
          Object.fromEntries(urlsByMarketplaceId)
        );
      }

      for (const marketplace of marketplacesToProcess) {
        // Determine URLs for this marketplace
        let urls = taskData.urls;

        // Keywords mode: generate URLs per marketplace from the resolved template
        if (taskData.input_mode === 'keywords' && taskData.keywords_text) {
          urls = generateSearchUrlsForMarketplace(marketplace, taskData.keywords_text);
          console.log('[Create Task] Generated URLs for', marketplace.name, ':', urls);
        }
        // URL mode with multiple marketplaces: use grouped URLs
        else if (urlsByMarketplaceId) {
          urls = urlsByMarketplaceId.get(marketplace.id) || [];
          console.log('[Create Task] Using grouped URLs for', marketplace.name, ':', urls);

          // Skip this marketplace if no URLs matched
          if (urls.length === 0) {
            console.log('[Create Task] Skipping', marketplace.name, '- no matching URLs');
            continue;
          }
        }

        const taskPayload = {
          ...taskData,
          marketplace_definition_id: marketplace.id,
          urls: urls,
          ...(createdBy ? { created_by: createdBy } : {}),
        };

        const newTask = await createTask(taskPayload);
        if (newTask) {
          // createTask returns the full task object with ID
          createdTasks.push(newTask);
          console.log(
            '[Create Task] Created task:',
            newTask.id,
            'for marketplace:',
            marketplace.id
          );
        } else {
          allSuccess = false;
          console.error('[Create Task] Failed to create task for marketplace:', marketplace.id);
        }
      }

      if (allSuccess) {
        // No need to fetchTasks - createTask already updated local state optimistically

        // If group creation was requested, create the group
        if (groupInfo?.createGroup && groupInfo.groupName && createdTasks.length > 1) {
          try {
            // Extract task IDs from the created tasks
            const taskIds = createdTasks.map(t => t.id);

            console.log('[Create Group] Creating group with task IDs:', taskIds);

            await createGroup({
              name: groupInfo.groupName,
              brand_name: taskData.brand_name || null,
              product_name: taskData.product_name || null,
              task_ids: taskIds,
            });

            setSnackMsg(
              `Created ${marketplacesToProcess.length} tasks and group "${groupInfo.groupName}"`
            );
            setSnackOpen(true);
          } catch (error) {
            console.error('[Create Group] Failed to create group:', error);
            setSnackMsg(`Created ${marketplacesToProcess.length} tasks but failed to create group`);
            setSnackOpen(true);
          }
        } else if (marketplacesToProcess.length > 1) {
          setSnackMsg(`Created ${marketplacesToProcess.length} tasks successfully`);
          setSnackOpen(true);
        }

        setCreateDialogOpen(false);
        // Refresh to get the group if created
        await fetchGroups();
      }
    } finally {
      setIsCreatingTasks(false);
    }
  };

  const handleAssembleSubmit = async (payload: AssembleSubmitPayload) => {
    setIsCreatingTasks(true);
    try {
      const createdBy = await getCurrentProviderId();
      const targetGroup = payload.appendToGroupId
        ? groups.find(g => g.id === payload.appendToGroupId)
        : null;
      const existingIds = targetGroup?.task_ids || [];
      const createdIds: string[] = [];
      let skipped = 0;
      const resolvedById: Record<string, any> = {};

      for (const marketplace of payload.marketplaces) {
        if (
          payload.appendToGroupId &&
          isActiveGroupDuplicate(
            tasks,
            existingIds,
            marketplace.definition_id,
            payload.keywordsText
          )
        ) {
          skipped += 1;
          continue;
        }

        if (!resolvedById[marketplace.definition_id]) {
          let definition: any = (definitions || []).find(
            (d: any) => d.id === marketplace.definition_id
          ) || {
            id: marketplace.definition_id,
            name: marketplace.name,
            domain: marketplace.domain,
          };
          try {
            const resolved = await fetchResolvedDefinition(marketplace.definition_id);
            definition = { ...definition, ...resolved };
          } catch (error) {
            console.warn(
              '[Assemble] Failed to resolve definition',
              marketplace.definition_id,
              error
            );
          }
          resolvedById[marketplace.definition_id] = definition;
        }
        const definition = resolvedById[marketplace.definition_id];

        const urls = generateSearchUrlsForMarketplace(definition, payload.keywordsText);
        if (!urls.length) {
          skipped += 1;
          continue;
        }

        const newTask = await createTask({
          name: [payload.brandName, marketplace.name].filter(Boolean).join(' '),
          marketplace_definition_id: marketplace.definition_id,
          urls,
          max_pages: marketplace.max_pages ?? 1,
          max_stage2_products: payload.maxStage2Products,
          input_mode: 'keywords',
          keywords_text: payload.keywordsText,
          brand_name: payload.brandName,
          paused: true,
          execution_mode: payload.executionMode,
          ...(createdBy ? { created_by: createdBy } : {}),
        });
        if (newTask?.id) {
          createdIds.push(newTask.id);
        }
      }

      if (payload.appendToGroupId) {
        if (createdIds.length) {
          await updateGroup(payload.appendToGroupId, {
            task_ids: [...existingIds, ...createdIds],
          });
        }
      } else if (createdIds.length) {
        await createGroup({
          name: payload.groupName,
          brand_name: payload.brandName,
          product_name: null,
          task_ids: createdIds,
        });
      }

      const parts = [`Created ${createdIds.length} paused task(s)`];
      if (skipped) parts.push(`skipped ${skipped} duplicate/empty`);
      setSnackMsg(parts.join(', '));
      setSnackOpen(true);
      setAssembleDialogOpen(false);
      setAssembleAppendGroup(null);
      setTabIndex(1);
      await fetchGroups();
      await fetchTasks();
    } finally {
      setIsCreatingTasks(false);
    }
  };

  const handleEditTask = async (taskData: any) => {
    if (!editingTask) return;

    // EditTaskDialog passes taskData directly (not wrapped in submitData)
    const success = await updateTask(editingTask.id, taskData);
    if (success) {
      setEditDialogOpen(false);
      setEditingTask(null);
      // Refresh the task list
      fetchTasks(handleTasksRefresh);
    }
  };

  const openEditDialog = (task: ScrapingTask) => {
    setEditingTask(task);
    setEditDialogOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'execution_mode',
      headerName: 'Mode',
      width: 76,
      minWidth: 76,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <ColumnHeaderTooltip
          label="Mode"
          tip="Run mode: Extension (Chrome) or Internal (cluster Playwright)"
        />
      ),
      renderCell: params => {
        const mode = params.row.execution_mode || 'internal';
        if (mode === 'extension') {
          const reason = params.row.extension_promoted_reason;
          const title =
            reason === 'captcha' ? 'Extension run (CAPTCHA fallback)' : 'Extension run (Chrome)';
          return (
            <IconCell title={title}>
              <Extension fontSize="small" color="warning" />
            </IconCell>
          );
        }
        return (
          <IconCell title="Internal run (cluster)">
            <Dns fontSize="small" color="action" />
          </IconCell>
        );
      },
    },
    {
      field: 'task_source',
      headerName: 'Source',
      width: 76,
      minWidth: 76,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <ColumnHeaderTooltip
          label="Source"
          tip="Where the task was created (Collector, Dashboard, Extension, etc.)"
        />
      ),
      renderCell: params => (
        <TaskSourceIconCell
          taskSource={params.row.task_source}
          crawlRequestId={params.row.crawl_request_id as string | undefined}
        />
      ),
    },
    {
      field: 'marketplace_definition',
      headerName: 'Marketplace',
      flex: 0.75,
      minWidth: 120,
      renderCell: params => {
        const definition = definitions?.find(d => d.id === params.row.marketplace_definition_id);
        const definitionId = params.row.marketplace_definition_id;
        const name = definition?.name || definitionId;
        return (
          <Tooltip title={name}>
            <Typography
              variant="body2"
              fontWeight="bold"
              color="primary"
              noWrap
              sx={{
                cursor: 'pointer',
                maxWidth: '100%',
                '&:hover': { opacity: 0.7 },
              }}
              onClick={e => {
                e.stopPropagation();
                navigate(`/definitions/${definitionId}/edit`);
              }}
            >
              {name}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'brand_product',
      headerName: 'Brand / Product',
      flex: 1.25,
      minWidth: 160,
      renderCell: params => (
        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
          {params.row.brand_name && (
            <Typography variant="body2" fontWeight="medium" noWrap>
              {params.row.brand_name}
            </Typography>
          )}
          {params.row.product_name && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {params.row.product_name}
            </Typography>
          )}
          {!params.row.brand_name && !params.row.product_name && (
            <Typography variant="caption" color="text.secondary" noWrap>
              —
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 76,
      minWidth: 76,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <ColumnHeaderTooltip
          label="Status"
          tip="Task status (queued, running, completed, failed, paused, partial)"
        />
      ),
      renderCell: params => {
        const task = params.row as ScrapingTask;
        const { label, color, Icon } = resolveTaskStatusDisplay(task);
        let tooltip = label;
        if (task.status === 'completed' && task.parsing_success_rate !== undefined) {
          const numRate = Math.round(task.parsing_success_rate * 100);
          tooltip = `${label} · ${numRate}% parsed`;
        }
        return (
          <IconCell title={tooltip}>
            <Icon fontSize="small" color={color} />
          </IconCell>
        );
      },
    },
    {
      field: 'success_rate',
      headerName: 'Success',
      width: 84,
      minWidth: 84,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => (
        <ColumnHeaderTooltip
          label="Success"
          tip="Data extraction success rate (% of listings parsed)"
        />
      ),
      renderCell: params => {
        const task = params.row;
        // Use parsing success rate if available, otherwise fall back to HTTP success rate
        const rate =
          task.parsing_success_rate !== undefined
            ? (task.parsing_success_rate * 100).toFixed(1)
            : (params.value * 100).toFixed(1);
        const numRate = parseFloat(rate);

        // Color based on success rate
        let color = 'text.primary';
        if (numRate === 100) color = 'success.main';
        else if (numRate >= 50) color = 'warning.main';
        else color = 'error.main';

        // Update tooltip based on which rate we're using
        const tooltipText =
          task.parsing_success_rate !== undefined
            ? 'Data extraction success rate based on actual listings found.'
            : 'HTTP request success rate. Check task details for parsing results.';

        return (
          <Tooltip title={tooltipText}>
            <Typography variant="body2" color={color}>
              {rate}%
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'created_by',
      headerName: 'Initiated by',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => {
        const providerId = params.row.created_by as string | null | undefined;
        const src = params.row.task_source as string | undefined;
        const fallbackLabel =
          !providerId &&
          (src === 'importer_crawl_request' ||
            src === 'dashboard_request' ||
            src === 'investigation_request' ||
            src === 'speak_up_request')
            ? 'Dashboard'
            : !providerId && src === 'collector_extension'
              ? 'Extension'
              : undefined;

        return (
          <UserProviderChip
            providerId={providerId}
            displayNames={userNames}
            namesLoading={userNamesLoading}
            fallbackLabel={fallbackLabel}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Started',
      width: 118,
      type: 'dateTime',
      valueFormatter: (params: { value: string }) => {
        if (!params.value) return '—';
        try {
          const d = new Date(params.value);
          return d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
        } catch {
          return params.value;
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      minWidth: 220,
      maxWidth: 220,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            gap: 0,
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'nowrap',
            width: '100%',
            '& .MuiIconButton-root': { p: 0.375 },
          }}
        >
          <Tooltip title="View Results">
            <span>
              <IconButton
                size="small"
                disabled={!['completed', 'failed'].includes(params.row.status)}
                onClick={() => navigate(`/tasks/${params.id}/results`)}
                color="primary"
              >
                <Visibility />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Edit Task">
            <span>
              <IconButton
                size="small"
                disabled={params.row.status === 'running' || params.row.status === 'queued'}
                onClick={() => openEditDialog(params.row as ScrapingTask)}
                color="secondary"
              >
                <Edit />
              </IconButton>
            </span>
          </Tooltip>

          {needsCaptchaSolving(params.row) && (
            <Tooltip title="Authentication/CAPTCHA Required - Click to resolve">
              <IconButton
                size="small"
                onClick={() => openCaptchaDialog(params.row)}
                color="warning"
              >
                <Security />
              </IconButton>
            </Tooltip>
          )}

          {params.row.status === 'paused' ? (
            <Tooltip title="Run Task">
              <IconButton
                size="small"
                onClick={async () => {
                  await startTask(params.id as string);
                  setLastRefresh(new Date());
                }}
                color="primary"
              >
                <PlayArrow />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Rerun Task">
              <span>
                <IconButton
                  size="small"
                  disabled={
                    params.row.status !== 'completed' &&
                    !(needsCaptchaSolving(params.row) && authExportCompleted[params.id as string])
                  }
                  onClick={async () => {
                    const newId = await rerunTask(params.id as string);
                    if (newId) {
                      // Optionally navigate to new task results
                    }
                    setLastRefresh(new Date());
                  }}
                  color="primary"
                >
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Delete Task">
            <IconButton
              size="small"
              onClick={async () => {
                const taskId = params.id as string;
                const ok = await deleteTask(taskId);
                if (ok) {
                  setLastRefresh(new Date());

                  // Clean up groups: remove this task from any groups it belongs to
                  const affectedGroups = groups.filter(g => g.task_ids.includes(taskId));
                  for (const group of affectedGroups) {
                    const updatedTaskIds = group.task_ids.filter(id => id !== taskId);

                    if (updatedTaskIds.length === 0) {
                      // Group is now empty, delete it
                      await deleteGroup(group.id);
                      console.log(`[Task Delete] Deleted empty group: ${group.name}`);
                    } else {
                      // Update group with remaining tasks
                      await updateGroup(group.id, { task_ids: updatedTaskIds });
                      console.log(`[Task Delete] Removed task from group: ${group.name}`);
                    }
                  }

                  if (affectedGroups.length > 0) {
                    await fetchGroups();
                  }
                }
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const groupColumns: GridColDef[] = [
    { field: 'name', headerName: 'Group', flex: 1, minWidth: 180 },
    { field: 'brand_name', headerName: 'Brand', width: 160 },
    { field: 'product_name', headerName: 'Product', width: 220 },
    {
      field: 'count',
      headerName: 'Tasks',
      width: 100,
      renderCell: params => {
        const taskIds: string[] = params.row.task_ids || [];
        const items = taskIds.map(id => {
          const t = tasks.find(tt => tt.id === id);
          if (!t) return `Task ${id}`;
          const u = t.urls && t.urls[0];
          let host = t.marketplace_definition_id;
          if (u) {
            try {
              host = new URL(u).hostname;
            } catch {
              void 0;
            }
          }
          const pages = t.max_pages ?? 1;
          const stage2 = t.max_stage2_products ?? 5;
          return `${host} (pages: ${pages}, stage2: ${stage2})`;
        });
        const count = taskIds.length;
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                {items.length === 0 ? (
                  <Typography variant="caption" color="inherit">
                    No tasks in group
                  </Typography>
                ) : (
                  items.map((txt, i) => (
                    <Typography key={i} variant="caption" display="block">
                      • {txt}
                    </Typography>
                  ))
                )}
              </Box>
            }
            enterDelay={400}
            arrow
          >
            <Typography variant="body2">{count}</Typography>
          </Tooltip>
        );
      },
    },
    { field: 'created_at', headerName: 'Created', width: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit group">
            <IconButton
              size="small"
              onClick={() => {
                setEditingGroup(params.row);
                setGroupDialogOpen(true);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            disabled={runningGroupId === params.row.id}
            onClick={async () => {
              const groupId = params.row.id as string;
              try {
                setRunningGroupId(groupId);
                const taskIds: string[] = params.row.task_ids || [];
                console.log('[Group Run] Task IDs:', taskIds);
                console.log('[Group Run] Available tasks:', tasks.length);

                // The tasks grid only holds the first page (queued/running first).
                // A new paused group is often entirely outside that page.
                const byId = new Map(tasks.map(task => [String(task.id), task]));
                const missing = taskIds.filter(id => !byId.has(String(id)));
                for (let i = 0; i < missing.length; i += 20) {
                  const loaded = await Promise.all(
                    missing.slice(i, i + 20).map(id => getTask(String(id)))
                  );
                  loaded.forEach(task => {
                    if (task?.id) byId.set(String(task.id), task);
                  });
                }

                let startedCount = 0;
                let rerunCount = 0;
                let notFoundCount = 0;
                let nextTaskIds = [...taskIds];

                for (const id of taskIds) {
                  const task = byId.get(String(id));
                  if (!task) {
                    console.warn(`[Group Run] Task ${id} not found`);
                    notFoundCount++;
                    continue;
                  }

                  console.log(`[Group Run] Task ${id} status: ${task.status}`);

                  // If task is paused or pending, start it; otherwise rerun it
                  if (task.status === 'paused' || task.status === 'pending') {
                    const success = await startTask(id, { refresh: false });
                    console.log(`[Group Run] startTask(${id}) result:`, success);
                    if (success) startedCount++;
                  } else {
                    const newId = await rerunTask(id, { refresh: false });
                    console.log(`[Group Run] rerunTask(${id}) result:`, newId);
                    if (newId) {
                      rerunCount++;
                      nextTaskIds = replaceGroupTaskId(nextTaskIds, id, newId);
                    }
                  }
                }

                if (rerunCount > 0) {
                  await updateGroup(params.row.id, { task_ids: nextTaskIds });
                }

                await fetchTasks();

                console.log(
                  `[Group Run] Summary - started: ${startedCount}, rerun: ${rerunCount}, not found: ${notFoundCount}`
                );

                if (startedCount === 0 && rerunCount === 0 && notFoundCount > 0) {
                  setSnackMsg(
                    `Could not find ${notFoundCount} task(s) from group "${params.row.name}"`
                  );
                } else {
                  const message =
                    startedCount > 0 && rerunCount > 0
                      ? `Started ${startedCount} and reran ${rerunCount} task(s) from group "${params.row.name}"`
                      : startedCount > 0
                        ? `Started ${startedCount} task(s) from group "${params.row.name}"`
                        : `Reran ${rerunCount} task(s) from group "${params.row.name}"`;
                  setSnackMsg(message);
                }
                setSnackOpen(true);

                // Switch to Tasks tab to watch execution
                setTabIndex(0);
              } catch (error) {
                console.error('[Group Run] Error:', error);
                setSnackMsg(`Error running group: ${error}`);
                setSnackOpen(true);
              } finally {
                setRunningGroupId(current => (current === groupId ? null : current));
              }
            }}
          >
            {runningGroupId === params.row.id ? 'Starting...' : 'Run'}
          </Button>
          <IconButton size="small" color="error" onClick={() => deleteGroup(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Auto-refresh: 5s" size="small" color="info" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          Last updated: {lastRefresh.toLocaleTimeString()}
        </Typography>
        {tabIndex === 0 && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {(
              [
                { label: 'Total', value: tasks.length, color: 'text.primary' },
                {
                  label: 'Running',
                  value: tasks.filter(t => t.status === 'running').length,
                  color: 'primary.main',
                },
                {
                  label: 'Completed',
                  value: tasks.filter(t => t.status === 'completed').length,
                  color: 'success.main',
                },
                {
                  label: 'Failed',
                  value: tasks.filter(t => t.status === 'failed').length,
                  color: 'error.main',
                },
              ] as const
            ).map(metric => (
              <Box key={metric.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {metric.label}
                </Typography>
                <Typography variant="body2" fontWeight={700} color={metric.color}>
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              label="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by task name..."
              sx={{ flex: '1 1 200px', minWidth: 180 }}
            />
            <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <InputLabel>Task source</InputLabel>
              <Select
                value={taskSourceFilter}
                onChange={e => setTaskSourceFilter(e.target.value as TaskSourceFilter)}
                label="Task source"
              >
                <MenuItem value="all">All sources</MenuItem>
                <MenuItem value="scraper_ui">UI</MenuItem>
                <MenuItem value="collector_extension">Extension</MenuItem>
                <MenuItem value="importer_crawl_request">Dashboard</MenuItem>
                <MenuItem value="dashboard_request">Dashboard (Investigation)</MenuItem>
                <MenuItem value="speak_up_request">Speak Up</MenuItem>
                <MenuItem value="definition_test_suite">Test suite</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 130px', minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="queued">Queued</MenuItem>
                <MenuItem value="running">Running</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <InputLabel>Marketplace</InputLabel>
              <Select
                value={marketplaceFilter}
                onChange={e => setMarketplaceFilter(e.target.value)}
                label="Marketplace"
              >
                <MenuItem value="all">All Marketplaces</MenuItem>
                {definitions?.map((def: any) => (
                  <MenuItem key={def.id} value={def.id}>
                    {def.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 'auto' }}>
              <Button size="small" variant="contained" onClick={() => setCreateDialogOpen(true)}>
                New Task
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={() => {
                  setAssembleAppendGroup(null);
                  setAssembleDialogOpen(true);
                }}
              >
                Assemble
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditingGroup(null);
                  setGroupDialogOpen(true);
                }}
              >
                New Group
              </Button>
              <Button
                size="small"
                variant={mineOnly ? 'contained' : 'outlined'}
                onClick={async () => {
                  if (mineOnly) {
                    setMineOnly(false);
                    return;
                  }
                  const id = myProviderId || (await getCurrentProviderId());
                  if (!id) {
                    setSnackMsg('Could not identify your user, so My tasks is unavailable');
                    setSnackOpen(true);
                    return;
                  }
                  setMyProviderId(id);
                  setMineOnly(true);
                }}
              >
                My tasks
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<Delete />}
                onClick={() => setDeletePendingOpen(true)}
              >
                Delete pending
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Tasks" />
          <Tab label="Groups" onClick={() => fetchGroups()} />
        </Tabs>
      </Box>

      {/* Tasks Table */}
      {tabIndex === 0 && (
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <DataGrid
              rows={filteredTasks}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
                sorting: {
                  sortModel: [{ field: 'created_at', sort: 'desc' }],
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                width: '100%',
                minWidth: '100%',
                border: 'none',
                '& .MuiDataGrid-root': {
                  width: '100%',
                  minWidth: '100%',
                },
                '& .MuiDataGrid-main': {
                  width: '100%',
                  minWidth: '100%',
                },
                '& .MuiDataGrid-virtualScroller': {
                  width: '100%',
                  minWidth: '100%',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                },
                '& .MuiDataGrid-cell[data-field="actions"]': {
                  overflow: 'visible',
                  px: 0.5,
                },
                '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                  px: 0.5,
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: isDarkMode ? 'grey.800' : 'grey.50',
                  borderBottom: '2px solid',
                  borderColor: isDarkMode ? 'grey.700' : 'grey.200',
                  color: isDarkMode ? 'grey.100' : 'grey.900',
                },
                '& .MuiDataGrid-columnHeader': {
                  color: isDarkMode ? 'grey.100' : 'grey.900',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: isDarkMode ? 'grey.100' : 'grey.900',
                  fontWeight: 600,
                },
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Groups Table */}
      {tabIndex === 1 && (
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <DataGrid
              rows={(groups || []).map(g => ({ ...g, id: g.id }))}
              columns={groupColumns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              autoHeight
              disableRowSelectionOnClick
              sx={{
                width: '100%',
                minWidth: '100%',
                border: 'none',
                '& .MuiDataGrid-root': {
                  width: '100%',
                  minWidth: '100%',
                },
                '& .MuiDataGrid-main': {
                  width: '100%',
                  minWidth: '100%',
                },
                '& .MuiDataGrid-virtualScroller': {
                  width: '100%',
                  minWidth: '100%',
                },
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateTask}
        definitions={scrapableDefinitions}
        loading={isCreatingTasks}
      />

      <EditTaskDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingTask(null);
        }}
        onSubmit={handleEditTask}
        task={editingTask}
        definitions={definitionsForTaskEdit}
      />

      <TaskGroupDialog
        open={groupDialogOpen}
        onClose={() => {
          setGroupDialogOpen(false);
          setEditingGroup(null);
        }}
        tasks={tasks}
        group={editingGroup}
        onSaved={() => fetchGroups()}
        onAssemble={current => {
          setGroupDialogOpen(false);
          setAssembleAppendGroup(current || editingGroup);
          setAssembleDialogOpen(true);
        }}
      />

      <AssembleGroupDialog
        open={assembleDialogOpen}
        onClose={() => {
          setAssembleDialogOpen(false);
          setAssembleAppendGroup(null);
        }}
        appendToGroup={assembleAppendGroup}
        onSubmit={handleAssembleSubmit}
        submitting={isCreatingTasks}
      />

      <Dialog
        open={deletePendingOpen}
        onClose={() => !deletingPending && setDeletePendingOpen(false)}
      >
        <DialogTitle>Delete pending tasks?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This deletes every waiting task (queued or pending), including ones that are not on this
            page. Running, paused, completed, and failed tasks stay. Brand scan and test-suite tasks
            stay.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePendingOpen(false)} disabled={deletingPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletingPending}
            onClick={async () => {
              setDeletingPending(true);
              const deleted = await deletePendingTasks();
              setDeletingPending(false);
              if (deleted == null) return;
              setDeletePendingOpen(false);
              setSnackMsg(
                deleted === 0 ? 'No pending tasks to delete' : `Deleted ${deleted} pending task(s)`
              );
              setSnackOpen(true);
              await fetchGroups();
            }}
          >
            {deletingPending ? 'Deleting...' : 'Delete pending'}
          </Button>
        </DialogActions>
      </Dialog>

      <VerifyAccessDialog
        open={captchaDialogOpen}
        task={captchaTask}
        onClose={() => setCaptchaDialogOpen(false)}
        onCheckStatus={async () => {
          const url = captchaTask?.urls?.[0];
          if (!url || !captchaTask?.id) return;

          const domain = new URL(url).hostname;
          const hasCookies = await checkCookieStatus(domain, captchaTask.id);
          setSnackMsg(
            hasCookies
              ? 'Auth export completed! You can now rerun this task.'
              : 'No cookies found. Please complete the auth export first.'
          );
          setSnackOpen(true);
          setCaptchaDialogOpen(false);
        }}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
};

export default ScrapingTasks;
